/**
 * Configuración → Gestión de NFC (`/configuracion/nfc`).
 *
 * Puerto de `Nfc_tags` + `Nfc_tags_model` + `views/nfc_tags/index.php`. El panel
 * anterior **sólo listaba** las etiquetas y permitía habilitarlas o
 * deshabilitarlas: las tarjetas se registran desde la app / el POS. Aquí se
 * mantiene igual (no hay alta ni baja).
 *
 * El dueño puede ser un usuario, una manguera o un cliente. El legacy rotulaba el
 * caso `pts_nozzle` como «Manguera N»; aquí se rotula **Lado N**, porque
 * `pts_nozzles.pumpNozzle` es el número de lado (el mismo dato que usa Monitoreo
 * en vivo).
 */

import { ArrowLeft, Info, Nfc, Power, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatDateTimeValue } from '@/lib/utils';

import { useMutarTagsNfc, useTagsNfc } from '../catalogos.api';
import type { TagNfc } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Nombre legible del tipo de dueño. */
const TIPO_DUENO: Record<TagNfc['tipo_dueno'], string> = {
  user: 'Usuario',
  customer: 'Cliente',
  pts_nozzle: 'Lado',
};

export function NfcPage() {
  useDocumentTitle('Configuración · Gestión de NFC');

  const tags = useTagsNfc();
  const mutaciones = useMutarTagsNfc();
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);

  const sinPermiso = tags.error instanceof ApiError && tags.error.status === 403;
  const lista = tags.data?.tags ?? [];

  const cabecera = (
    <header>
      <Link
        to="/configuracion"
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
      >
        <ArrowLeft className="size-3" aria-hidden="true" />
        Configuración
      </Link>
      <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
        <Nfc className="size-6 text-brand-500" aria-hidden="true" />
        Gestión de NFC
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-ink-500">
        Tarjetas NFC que identifican al cliente o al grifero en el POS. Se registran desde la app;
        aquí se habilitan o deshabilitan.
      </p>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const cambiarEstado = (tag: TagNfc) => {
    mutaciones.cambiarEstado.mutate(
      { id: tag.id, activo: !tag.activo },
      {
        onSuccess: (datos) => setAviso({ tono: 'success', texto: datos.mensaje }),
        onError: (error) =>
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {tags.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : tags.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los dispositivos NFC">
          {mensajeDe(tags.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Dispositivos registrados</CardTitle>
            <Badge variant="neutral">{tags.data?.total ?? 0} registrados</Badge>
          </CardHeader>
          <CardContent>
            {lista.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-500">
                Todavía no hay dispositivos NFC registrados. Se dan de alta desde la app, cuando se
                enlaza una tarjeta a un cliente, a un usuario o a una manguera.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[48rem] border-collapse text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                      <th className="pb-2">NFC</th>
                      <th className="w-44 pb-2">Dueño</th>
                      <th className="w-28 pb-2">Tipo</th>
                      <th className="w-44 pb-2">Dispositivo</th>
                      <th className="w-44 pb-2">Último uso</th>
                      <th className="w-24 pb-2">Estado</th>
                      <th className="w-24 pb-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.map((tag) => (
                      <tr key={tag.id} className="border-t border-ink-100">
                        <td className="py-3">
                          <span className="rounded-control bg-ink-100 px-2 py-0.5 font-mono text-xs font-semibold text-ink-700">
                            {tag.nfc_id}
                          </span>
                        </td>
                        <td className="py-3 font-medium text-ink-900">{tag.dueno}</td>
                        <td className="py-3">
                          <Badge variant="outline">{TIPO_DUENO[tag.tipo_dueno]}</Badge>
                        </td>
                        <td className="py-3 text-ink-600">{tag.device_name || '—'}</td>
                        <td className="py-3 text-xs text-ink-500">
                          {tag.ultimo_uso ? formatDateTimeValue(tag.ultimo_uso) : 'Nunca'}
                        </td>
                        <td className="py-3">
                          <Badge variant={tag.activo ? 'success' : 'neutral'}>
                            {tag.activo ? 'Habilitado' : 'Deshabilitado'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={
                                tag.activo ? `Deshabilitar ${tag.nfc_id}` : `Habilitar ${tag.nfc_id}`
                              }
                              title={tag.activo ? 'Deshabilitar' : 'Habilitar'}
                              loading={mutaciones.cambiarEstado.isPending}
                              onClick={() => cambiarEstado(tag)}
                            >
                              <Power
                                className={`size-4 ${tag.activo ? 'text-emerald-600' : 'text-ink-400'}`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Alert variant="info" className="mt-4" title="Sobre las tarjetas NFC">
              <span className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  El alta y la baja de tarjetas se hacen desde la app: aquí sólo se consultan y se
                  habilitan. Una tarjeta deshabilitada deja de identificar al cliente en el POS.
                </span>
              </span>
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
